import posthog from "posthog-js";
import { robotItemsAdded, trackOpenAI } from "@/analytics/openai";
import { trackMetaAddToCart } from "@/analytics/meta";
import { trackPinterest } from "@/analytics/pinterest";

import { addToOrCreateCart } from "@/cart/addItem";
import { mergeCartAttributes, type CartAttribute } from "@/cart/cartAttributes";
import {
  getReferralCode,
  syncReferralRewardWithRollback,
} from "@/cart/maticReferralReward";
import { lookUpReferralReward } from "@/cart/referralReward";
import type { AddItemContext, CartStateGetSet } from "@/cart/types";
import {
  MATIC_BAG_PASS_VARIANT_ID,
  MATIC_REFERRAL_BAG_PASS_VARIANT_ID,
  MATIC_REFKIT_VARIANT_ID,
} from "@/consts/shopify";
import type { Cart, CartLineInput } from "@/graphql/storefront/generated";
import { metaAddToCartData } from "@/lib/metaConversions";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";

export type AddItemsLine = {
  variantId: string;
  quantity: number;
  attributes?: readonly CartAttribute[];
};

type AddItemsOptions = {
  codeOverride?: string;
  /** Set on the cart alongside the lines, over whatever it already carries. */
  cartAttributes?: Record<string, string>;
};

// Adds several lines in a single cart mutation (one cartCreate/cartLinesAdd)
// instead of a round-trip per line, then runs the referral reward sync once.
export const addItems = ({ get, set }: CartStateGetSet) => {
  const syncReward = syncReferralRewardWithRollback({ get, set });

  return async (
    items: AddItemsLine[],
    context?: AddItemContext,
    options?: AddItemsOptions,
  ) => {
    const referralCode = getReferralCode(
      get,
      get().cart,
      options?.codeOverride,
    );

    if (referralCode) {
      await get().ensureReferralCode(referralCode);
    }

    const reward = referralCode
      ? await lookUpReferralReward(referralCode)
      : undefined;

    const cartBeforeAdd = get().cart;

    const lines: CartLineInput[] = items
      .filter((item) => item.quantity > 0)
      .filter(
        (item) =>
          item.variantId !== MATIC_REFKIT_VARIANT_ID &&
          item.variantId !== MATIC_REFERRAL_BAG_PASS_VARIANT_ID &&
          // A code that gives the referral-edition pass gets it through the
          // reward sync, so the standard pass is never added alongside it.
          !(
            item.variantId === MATIC_BAG_PASS_VARIANT_ID &&
            reward === "referral-bag-pass"
          ),
      )
      .map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
        attributes: [...(item.attributes ?? [])],
      }));

    if (lines.length === 0) {
      return get().cart;
    }

    const cart = await addToOrCreateCart(
      lines,
      get().cartId,
      options?.cartAttributes === undefined
        ? undefined
        : mergeCartAttributes(cartBeforeAdd, options.cartAttributes),
    );

    if (!cart) {
      throw new Error("addItems: Adding lines to cart failed");
    }

    set({ cartId: cart.id, cart });

    for (const line of lines) {
      posthog.capture("add_item", {
        variantId: line.merchandiseId,
        cartId: cart.id,
        source: context?.source,
        button_location: context?.buttonLocation,
      });

      trackMetaAddToCart(() => {
        const addedLine = cart.lines.edges.find(
          ({ node }) => node.merchandise.id === line.merchandiseId,
        )?.node;
        return addedLine === undefined
          ? null
          : metaAddToCartData(addedLine, line.quantity ?? 1);
      });
    }

    trackPinterest("addtocart", () => {
      const addedLines = lines.flatMap((line) => {
        const node = cart.lines.edges.find(
          ({ node }) => node.merchandise.id === line.merchandiseId,
        )?.node;
        return node === undefined
          ? []
          : [{ node, quantity: line.quantity ?? 1 }];
      });
      const [firstLine] = addedLines;
      if (firstLine === undefined) return null;
      return {
        value: addedLines.reduce(
          (sum, { node, quantity }) =>
            sum + Number(node.merchandise.price.amount) * quantity,
          0,
        ),
        order_quantity: addedLines.reduce(
          (sum, { quantity }) => sum + quantity,
          0,
        ),
        currency: firstLine.node.merchandise.price.currencyCode,
        line_items: addedLines.map(({ node, quantity }) => ({
          product_id: node.merchandise.product.id.split("/").pop() ?? "",
          product_quantity: quantity,
        })),
      };
    });

    trackOpenAI("items_added", () =>
      robotItemsAdded(
        lines.flatMap((line) => {
          const node = cart.lines.edges.find(
            ({ node }) => node.merchandise.id === line.merchandiseId,
          )?.node;
          return node === undefined
            ? []
            : [{ line: node, quantity: line.quantity ?? 1 }];
        }),
      ),
    );

    const hasMatic = lines.some((line) =>
      isMaticFamilyVariantId(line.merchandiseId),
    );

    if (hasMatic && getReferralCode(get, cart, options?.codeOverride)) {
      return syncReward(cartBeforeAdd, cart, options?.codeOverride);
    }

    return cart as Cart;
  };
};
